import { useTranslation } from "react-i18next";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import type { DailySnapshot } from "@/mocks/dashboardCharts";

const recentDays = 14;

interface DailyActivityChartProps {
  data: DailySnapshot[];
}

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-background-50 border border-background-200/70 rounded-xl px-4 py-3 shadow-lg">
      <p className="text-xs text-foreground-500 mb-1">{label}</p>
      {payload.map((entry: any, i: number) => (
        <p key={i} className="text-sm font-semibold" style={{ color: entry.color }}>
          {entry.name}: {entry.value.toLocaleString()}
        </p>
      ))}
    </div>
  );
}

export default function DailyActivityChart({ data }: DailyActivityChartProps) {
  const { t } = useTranslation("dashboard");
  const chartData = data.slice(-recentDays);

  return (
    <div className="bg-background-50 rounded-2xl border border-background-200/50 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading text-lg text-foreground-950">{t("charts.dailyActivity")}</h3>
          <p className="text-xs text-foreground-500 mt-0.5">{t("charts.inputOutputPerDay")}</p>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-secondary-500"></span>
            <span className="text-foreground-600">{t("charts.input")}</span>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-primary-500"></span>
            <span className="text-foreground-600">{t("charts.output")}</span>
          </span>
        </div>
      </div>
      <div className="h-[240px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }} barGap={4}>
            <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--foreground-200) / 0.4)" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: "oklch(var(--foreground-400))" }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "oklch(var(--foreground-400))" }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => `${(v / 1000).toFixed(1)}k`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="inputTokens" name={t("charts.input")} fill="oklch(var(--secondary-500))" radius={[6, 6, 0, 0]} maxBarSize={24} />
            <Bar dataKey="outputTokens" name={t("charts.output")} fill="oklch(var(--primary-500))" radius={[6, 6, 0, 0]} maxBarSize={24} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}